/**
 * The local record state machine.
 *
 * ```
 *                     ┌────────┐
 *    user completes   │ LOCAL  │  written to IndexedDB, receipt printable
 *    a sale     ──────►└───┬────┘  (exists even with no network, ever)
 *                         │ enqueued in the outbox
 *                     ┌───▼─────┐
 *                     │ PENDING │  waiting for connectivity / backoff
 *                     └───┬─────┘
 *              sync engine │ claims it, POSTs the batch
 *                     ┌───▼─────┐
 *                     │ SYNCING │  in flight — NOT yet safe to delete
 *                     └──┬───┬──┘
 *       ACCEPTED /       │   │        network error / 5xx / timeout
 *       DUPLICATE ───────┘   └──────────────┐
 *               │                            │
 *         ┌─────▼─────┐              ┌───────▼────────┐
 *         │  SYNCED   │              │    FAILED      │ attempts++, backoff
 *         └───────────┘              └───────┬────────┘
 *                                            │ after MAX_ATTEMPTS
 *         4xx rejection or          ┌────────▼────────┐
 *         server conflict ─────────►│    CONFLICT     │ needs review.
 *                                   └─────────────────┘ NEVER discarded.
 * ```
 *
 * The rule underneath all of it: a record reaches SYNCED **only** when the
 * server's per-operation acknowledgement has been written to IndexedDB. If the
 * response is lost in flight the operation stays SYNCING, times out back to
 * PENDING (a claim is a lease: `CLAIM_LEASE_MS` in outboxRepository), retries, and the server's idempotency check returns DUPLICATE — which
 * is treated as success, because from the client's point of view the outcome is
 * exactly what it asked for.
 */

import type { SyncState } from "@/db/schema";

/** Terminal states: the engine will not touch these again unaided. */
export const TERMINAL_STATES: readonly SyncState[] = ["SYNCED", "CONFLICT"] as const;

/** States whose records must never be purged from local storage. */
export const UNACKNOWLEDGED_STATES: readonly SyncState[] = [
  "LOCAL",
  "PENDING",
  "SYNCING",
  "FAILED",
  "CONFLICT",
] as const;

/** After this many attempts an operation is dead-lettered for human review. */
export const MAX_ATTEMPTS = 12;

/** Legal transitions. Anything else is a bug, and `assertTransition` says so. */
const ALLOWED: Record<SyncState, readonly SyncState[]> = {
  LOCAL: ["PENDING"],
  PENDING: ["SYNCING"],
  // SYNCING → PENDING is the lost-response path: the request timed out, so we
  // do not know whether the server committed. Retrying is safe precisely because
  // the idempotency key makes a duplicate upload a no-op.
  SYNCING: ["SYNCED", "FAILED", "CONFLICT", "PENDING"],
  FAILED: ["PENDING", "CONFLICT"],
  // A conflict is resolved by a human, which re-queues the operation.
  CONFLICT: ["PENDING"],
  SYNCED: [],
};

export function canTransition(from: SyncState, to: SyncState): boolean {
  return ALLOWED[from].includes(to);
}

export function assertTransition(from: SyncState, to: SyncState): void {
  if (!canTransition(from, to)) {
    throw new Error(
      `Illegal sync transition ${from} → ${to}. ` +
        "A record may only reach SYNCED via a server acknowledgement.",
    );
  }
}

/** True while the record still needs to reach the server. */
export function isUnacknowledged(state: SyncState): boolean {
  return UNACKNOWLEDGED_STATES.includes(state);
}

/**
 * Whether a local record may be purged.
 *
 * Both conditions are required. Acknowledgement alone is not enough: a cashier
 * must be able to reprint yesterday's receipt with no connectivity, so
 * acknowledged sales are kept for a retention window first.
 */
export function isPurgeable(
  state: SyncState,
  acknowledgedAt: string | null,
  retentionDays = 7,
): boolean {
  if (state !== "SYNCED" || !acknowledgedAt) return false;
  const ageMs = Date.now() - new Date(acknowledgedAt).getTime();
  return ageMs > retentionDays * 24 * 60 * 60 * 1000;
}

// ═════════════════════════════════════════════════════════════════════════════
// Retry backoff
// ═════════════════════════════════════════════════════════════════════════════

/** 5s, 15s, 60s, 5m, 15m, 1h — then 1h for every further attempt. */
const BACKOFF_LADDER_MS = [5_000, 15_000, 60_000, 300_000, 900_000, 3_600_000] as const;

/**
 * When to retry after `attempts` failures.
 *
 * Jitter matters more than usual here: a shop that loses power brings twenty
 * terminals back online in the same second, and an unjittered ladder would have
 * all of them retry in lockstep for the rest of the day.
 */
export function backoffDelayMs(attempts: number, jitter = Math.random): number {
  const index = Math.min(Math.max(attempts - 1, 0), BACKOFF_LADDER_MS.length - 1);
  const base = BACKOFF_LADDER_MS[index]!;
  // ±20%
  return Math.round(base * (0.8 + jitter() * 0.4));
}

/** An operation past MAX_ATTEMPTS is dead-lettered — surfaced, never dropped. */
export function isExhausted(attempts: number): boolean {
  return attempts >= MAX_ATTEMPTS;
}

// ═════════════════════════════════════════════════════════════════════════════
// UI status
// ═════════════════════════════════════════════════════════════════════════════

/** What the status chip in the top bar shows. */
export type ConnectionStatus =
  | "ONLINE"
  | "OFFLINE"
  | "SYNCING"
  | "SYNC_FAILED"
  | "CHECKING";

/**
 * Derive the single status the cashier sees.
 *
 * Order is deliberate. A failed sync outranks "offline", because a merchant whose
 * takings are stuck needs to know that even while the connection is down —
 * "Offline" alone would read as "nothing to worry about, it will catch up".
 */
export function deriveStatus(input: {
  reachable: boolean | null;
  syncing: boolean;
  deadLetterCount: number;
}): ConnectionStatus {
  if (input.deadLetterCount > 0) return "SYNC_FAILED";
  if (input.reachable === null) return "CHECKING";
  if (!input.reachable) return "OFFLINE";
  if (input.syncing) return "SYNCING";
  return "ONLINE";
}
